import { EventBus } from "./EventBus";

import Vue from "vue";
import Store from "./Store";
import { getProposalsForSelection } from "../ProposalSelection";

export default class EventManager {
  constructor() {
    this._vue = new Vue({
      store: Store,
    });

    EventBus.$on("select-district-from-url", this.selectFromUrl.bind(this));

    let self = this;
    this._vue.$map.once("idle", () => {
      EventBus.$on(
        "select-from-address-search",
        self.selectFromAddressSearch.bind(self),
      );
      EventBus.$on(
        "select-from-district-search",
        self.selectFromDistrictSearch.bind(self),
      );
      EventBus.$on("select-from-click", self.selectFromClick.bind(self));
      EventBus.$on(
        "select-from-type-switch",
        self.selectFromTypeSwitch.bind(self),
      );
      EventBus.$on(
        "select-from-proposal-switch",
        self.selectFromProposalSwitch.bind(self),
      );

      EventBus.$on("clear-selected-districts", self.unselect.bind(self));

      //EventBus.$on("get-block-group-vap", self.retrieveVAPdata.bind(self));
      //EventBus.$on("vap-data-loaded", self.styleVAPLayer.bind(self));
      //EventBus.$on("get-vote-results", self.retrieveVoteResultdata.bind(self));

      //EventBus.$on("vote-data-loaded", self.styleVoteLayer.bind(self));
      EventBus.$on("set-modal", self.modalTabSet.bind(self));
    });
  }

  selectDistrictOnMapFromLatLng(lnglat, shouldZoom, selectionType) {
    /** calls updateMapSelection, has a built in debounce to avoid calling the method too many times */
    const params = { lnglat, shouldZoom, selectionType };
    const execute = () => {
      if (this._vue.$map.isStyleLoaded()) {
        this.updateMapSelection(params);
      } else {
        this._vue.$map.once("idle", () => this.updateMapSelection(params));
      }
    };

    // Execute immediately if no pending call
    if (!this._selectionDebounceTimer) {
      execute();
    }

    // Clear and reset debounce timer
    clearTimeout(this._selectionDebounceTimer);
    this._selectionDebounceTimer = setTimeout(() => {
      this._selectionDebounceTimer = null;
    }, 150);
  }

  unselect() {
    const [sideAid, sideBid] = [
      this._vue.$store.getters.getSideAid,
      this._vue.$store.getters.getSideBid,
    ];
    this._vue.$layerManager.unhighlightDistricts(sideAid, "A");
    this._vue.$layerManager.unhighlightDistricts(sideBid, "B");

    this._vue.$querystringManager.removeQueryParam("selected");

    EventBus.$emit("clearAddress");
    EventBus.$emit("clearSidePanels");
    this._vue.$store.commit("resetSelection");
  }

  async _fetchDistrict(propName, lat, lng) {
    try {
      const res = await this._vue.$dataManager.getDistrictFromLatLng(
        propName,
        lat,
        lng,
      );
      const record = res?.data?.[0];
      if (!record) {
        console.warn(
          `No district record found for ${propName} at [${lat}, ${lng}]`,
        );
        return null;
      }
      return record;
    } catch (e) {
      console.error("Failed to fetch district data for", propName, e);
      return null;
    }
  }

  /**
   * Attempts to resolve a district for the given propId and side. If no geoid
   * is found, falls back to the configured default proposal for that side and
   * district type, swapping the store/layer/URL state as needed.
   *
   * Returns { propId, propData } — propId may differ from the input when a
   * fallback swap occurs. Returns null if no district could be resolved.
   */
  async _resolveDistrict(propId, side, lat, lng) {
    let propData = await this._fetchDistrict(propId, lat, lng);

    if (!propData?.geoid) {
      const districtType = this._vue.$store.getters.getActiveDistrictType;
      const selectedState = this._vue.$stateData?.states?.find(
        (state) =>
          state.statefips === this._vue.$store.getters.getSelectedState,
      );
      const stateAbb = selectedState?.stateabb?.toLowerCase() ?? null;

      const proposals = getProposalsForSelection(
        this._vue.$pgConfig,
        districtType,
        stateAbb,
      );

      // filter for a valid proposal, perf to get one with defaultdisplay === side
      const validProposals = proposals.filter(
        (d) =>
          (d.defaultdisplay === side || d.defaultdisplay === "") &&
          d.tablename !== propId,
      );

      if (!validProposals.length) return null;
      const defaultProposal = validProposals[0]

      console.error(
        `No district here for ${propId}, trying fallback: ${defaultProposal.tablename}`,
      );

      propData = await this._fetchDistrict(defaultProposal.tablename, lat, lng);

      if (!propData?.geoid) {
        console.error(`Fallback fetch also failed for side ${side}`);
        return null;
      }

      const { tablename, displayname, proposal_id } = defaultProposal;

      EventBus.$emit("swap-proposal", displayname);
      propId = tablename;

      this._vue.$store.commit(
        side === "A" ? "setSideAid" : "setSideBid",
        tablename,
      );
      this._vue.$store.commit(
        side === "A" ? "setSideAProposal" : "setSideBProposal",
        defaultProposal,
      );
      this._vue.$store.commit(
        side === "A" ? "setSideADisplayName" : "setSideBDisplayName",
        displayname,
      );

      this._vue.$layerManager.showProposal({ _id: tablename, _side: side });
      this._vue.$querystringManager.addQueryParam(
        side === "A" ? "propA" : "propB",
        proposal_id,
      );
    }

    return { propId, propData };
  }

  _applyDistrictSelection(propId, propData, side) {
    this._vue.$layerManager.highlightDistrict(propId, propData.geoid, side);
    EventBus.$emit(`update-side-panel-${side}`, {
      geoid: propData.geoid,
      propid: propId,
      side,
    });
  }

  _clearDistrictSelection(propId, side) {
    this._vue.$layerManager.unhighlightDistricts(propId, side);
    this._vue.$store.commit(
      side === "A" ? "setSelectedDistrictSideA" : "setSelectedDistrictSideB",
      "",
    );
    EventBus.$emit(`update-side-panel-${side}`, {
      geoid: "",
      propid: propId,
      side,
    });
  }

  _sleep(ms = 0) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async updateMapSelection(options) {
    const [lng, lat] = options.lnglat;
    if (lng == null || lat == null) return;

    // reset marker
    EventBus.$emit("clear-selected-districts");
    EventBus.$emit("add-marker", lat, lng);

    this._vue.$nextTick(async () => {
      const sides = [
        { side: "A", getId: () => this._vue.$store.getters.getSideAid },
        { side: "B", getId: () => this._vue.$store.getters.getSideBid },
      ];

      for (const { side, getId } of sides) {
        const propId = getId();
        if (!propId) continue;

        const resolved = await this._resolveDistrict(propId, side, lat, lng);

        // If _resolveDistrict swapped the proposal, wait for Vue to flush so onProposalChange is ran
        if (resolved?.propId !== propId) {
          await this._sleep(100);
        }

        if (resolved) {
          this._applyDistrictSelection(
            resolved.propId,
            resolved.propData,
            side,
          );
        } else {
          this._clearDistrictSelection(propId, side);
        }
      }

      if (options.shouldZoom) {
        this._vue.$map.once("idle", () => EventBus.$emit("zoom-to-districts"));
      }
    });
  }

  selectFromAddressSearch(lnglat) {
    this.selectDistrictOnMapFromLatLng(
      lnglat,
      true,
      "select-from-address-search",
    );
  }

  selectFromDistrictSearch(lnglat, shouldZoom = true) {
    this.selectDistrictOnMapFromLatLng(
      lnglat,
      shouldZoom,
      "select-from-district-search",
    );
  }

  selectFromClick(lnglat) {
    this.selectDistrictOnMapFromLatLng(lnglat, false, "select-from-click");
  }

  selectFromTypeSwitch(lnglat) {
    //if (this.$dataManager.waitForUrl) return;
    this.selectDistrictOnMapFromLatLng(
      lnglat,
      false,
      "select-from-type-switch",
    );
  }

  selectFromProposalSwitch(lnglat) {
    this.selectDistrictOnMapFromLatLng(
      lnglat,
      false,
      "select-from-proposal-switch",
    );
  }

  selectFromUrl(lnglat) {
    this.selectDistrictOnMapFromLatLng(lnglat, false, "select-from-url");
  }

  retrieveVAPdata() {
    this._vue.$dataManager.retrieveVAPdata();
  }

  styleVAPLayer() {
    this._vue.$layerManager.styleVAPLayer();
  }

  styleVoteLayer() {
    this._vue.$layerManager.styleVoteLayer();
  }
  retrieveVoteResultdata() {
    this._vue.$dataManager.retrieveVoteResultdata();
  }

  modalTabSet(val) {
    switch (val) {
      case "print":
        break;
      default:
        break;
    }
  }
}
